import type { ApexOptions } from 'apexcharts'
import type { ChartCard } from '@/features/dashboard/api/schemas'
import { niceAxis, tickLabel } from '@/features/dashboard/format/axis'
import {
  chartTotals,
  formatAbbrev,
  formatMonthLong,
  formatMonthShort,
} from '@/features/dashboard/format/format'
import {
  boldLabel,
  centerGoalLabel,
  dataPointIndexOf,
  extendGoalLine,
  liftBarsOffAxis,
  shapeBars,
  TOP_RADIUS_PX,
} from './apexDom'
import { baseOptions, type ChartPalette } from './chartTheme'
import { tooltipHtml } from './tooltipHtml'

const MAX_BAR_PX = 120
const BAR_INSET_PX = 8

/** What the expanded chart plots, derived from its card alone. */
export const expandedBarModel = (card: ChartCard) => {
  // Multiple series are summed into one bar per category.
  const totals = chartTotals(card)
  const values = card.categories.map(
    (_, i) =>
      totals[i] ??
      card.series.reduce((sum, series) => sum + (series.data[i] ?? 0), 0)
  )
  const goal = card.goalValue ?? null
  const lo = Math.min(0, ...values)
  // The target only ever raises the top of the axis, never the bottom.
  const hi = Math.max(0, ...values, ...(goal === null ? [] : [goal]))
  return {
    values,
    labels: card.categories.map(formatMonthShort),
    goal,
    goalText:
      goal === null ? '' : `Global target: ${formatAbbrev(goal, card.unit, 2)}`,
    axis: niceAxis(lo, hi),
    // Bars grow from 0, so only an all-positive chart lifts off the axis.
    allPositive: lo >= 0,
  }
}

export type ExpandedBarModel = ReturnType<typeof expandedBarModel>

/** The width of a bar, given the chart's width and how many bars share it. */
export const barWidthPx = (width: number, bars: number) =>
  Math.max(
    1,
    Math.min(MAX_BAR_PX, Math.floor(width / Math.max(bars, 1) - BAR_INSET_PX))
  )

export const expandedBarOptions = (
  card: ChartCard,
  model: ExpandedBarModel,
  palette: ChartPalette,
  barPx: number
): ApexOptions => {
  const { values, labels, goal, goalText, axis, allPositive } = model
  const base = baseOptions(palette)

  // Lifts the bars and lays the target line and label out over Apex's own.
  const decorate = (chart: unknown) => {
    if (allPositive) liftBarsOffAxis(chart)
    if (goal !== null) {
      extendGoalLine(chart)
      centerGoalLabel(chart)
    }
  }

  return {
    ...base,
    chart: {
      ...base.chart,
      type: 'bar',
      zoom: { enabled: false },
      selection: { enabled: false },
      animations: {
        easing: 'easeinout',
        speed: 500,
        animateGradually: { enabled: true, delay: 80 },
        dynamicAnimation: { enabled: false },
      },
      events: {
        mounted: decorate,
        updated: (chart: unknown) => {
          decorate(chart)
          if (allPositive) shapeBars(chart)
        },
        ...(allPositive && { animationEnd: shapeBars }),
        dataPointMouseEnter: (_event, chart, options) => {
          const index = dataPointIndexOf(options)
          boldLabel(chart, index === null ? null : (labels[index] ?? null))
        },
        dataPointMouseLeave: (_event, chart) => boldLabel(chart, null),
      },
    },
    colors: [palette.bar],
    plotOptions: {
      bar: {
        borderRadius: Math.min(TOP_RADIUS_PX, barPx / 2),
        borderRadiusApplication: 'end',
        columnWidth: `${barPx}px`,
      },
    },
    // The hover colour comes from the CSS, since Apex's darken filter all but
    // hides the sand bars in dark mode.
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    grid: {
      show: true,
      borderColor: palette.barGrid,
      strokeDashArray: 6,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      // Room for the target's label above the tallest bar.
      padding: { left: 8, right: 0, bottom: 0, top: goal === null ? 0 : 24 },
    },
    xaxis: {
      categories: labels,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        style: { colors: palette.textAlt, fontSize: '12px' },
      },
      axisBorder: { show: true, color: palette.baseline },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      crosshairs: { show: false },
    },
    yaxis: {
      min: axis.min,
      max: axis.max,
      tickAmount: axis.ticks,
      labels: {
        style: { colors: palette.textAlt, fontSize: '12px' },
        formatter: tickLabel,
      },
    },
    legend: { show: false },
    annotations:
      goal === null
        ? {}
        : {
            // A solid line with its label centred in a gap (see centerGoalLabel).
            yaxis: [
              {
                y: goal,
                borderColor: palette.goal,
                strokeDashArray: 0,
                label: {
                  text: goalText,
                  position: 'left',
                  textAnchor: 'middle',
                  offsetY: 7,
                  borderWidth: 0,
                  style: {
                    background: palette.card,
                    color: palette.goal,
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: { left: 8, right: 8, top: 2, bottom: 2 },
                  },
                },
              },
            ],
          },
    tooltip: {
      enabled: true,
      followCursor: true,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) =>
        tooltipHtml({
          label: formatMonthLong(card.categories[dataPointIndex] ?? ''),
          value: values[dataPointIndex] ?? 0,
          previous:
            dataPointIndex > 0 ? (values[dataPointIndex - 1] ?? null) : null,
          unit: card.unit,
          palette,
        }),
    },
  }
}
